"use client"

import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import Image from "next/image"
import { useState } from "react"
import { Autoplay, EffectFade } from "swiper/modules"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { Container, LeftArrow, RightArrow, transitionTime, useIsDesktop } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledContainer = styled(Container)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  align-items: center;
  p {
    margin: 0px;
    margin-bottom: 8px;
  }
  padding-top: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 40px;
  }
`
const StyledContainerInner = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 400px;
    width: 800px;
  }
`

const StyledPaginatorContainer = styled.div`
  z-index: 100;
  padding: 8px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    border-radius: 200px;

    bottom: 55px;
    width: fit-content;
    background-color: white;
    position: relative;
    left: 50%;
    top: auto;
    transform: translateX(-50%);
  }
`

const StyledBulletsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 0 4px;
`

const StyledBullet = styled.div<{ active: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin: 0 3.335px;
  cursor: pointer;
  border: ${(props) =>
    props.active
      ? `1.67px solid ${props.theme.colors.bulletsBackgoundColor}`
      : "1.67px solid black"};
  background-color: ${(props) =>
    props.active ? props.theme.colors.bulletsBackgoundColor : "none"};
`
const StyledVideoContainer = styled.div`
  opacity: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  position: absolute;
  ${(props) => props.theme.breakpoints.up("md")} {
  }
`
const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  flex-direction: column;
  > div {
    position: relative;
    flex-grow: 1;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    > div {
      position: relative;
      flex-grow: 1;
    }
  }
`

function SliderItem({
  item,
}: {
  item: {
    url: string
    type: "MEDIA" | "EXTERNAL"
  }
  active: boolean
}) {
  const { type, url } = item
  let videoId: string | undefined
  if (type === "EXTERNAL") {
    videoId = url.split("/").pop()?.split("?")[0]
  }

  return (
    <>
      {type === "EXTERNAL" && (
        <StyledVideoContainer>
          <iframe
            style={{ width: "100%", height: "100%" }}
            src={`https://www.youtube.com/embed/${videoId}`}
          ></iframe>
        </StyledVideoContainer>
      )}
      {type === "MEDIA" && (
        // No need to change image component it's different from image with alt
        <Image
          style={{ objectFit: "cover" }}
          src={getUmbracoMediaUrl(url, true)}
          alt={""}
          fill
          priority
        />
      )}
    </>
  )
}

export default function ArticleGallery({ data, culture }: { data: any; culture: string }) {
  const isDesktop = useIsDesktop()
  const [swiperRef, setSwiperRef] = useState<any>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const galleryElements =
    (extractProperty(data, "galleryElements") as {
      url: string
      type: "MEDIA" | "EXTERNAL"
    }[]) || []
  return (
    <StyledContainer withoutPadding>
      <StyledContainerInner>
        <Swiper
          onSwiper={setSwiperRef}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)
          }}
          style={{
            padding: 0,
            borderRadius: isDesktop ? 16 : 0,
            height: "100%",
          }}
          speed={transitionTime * 1000}
          modules={[EffectFade, Autoplay]}
          effect="fade"
          autoplay={false}
        >
          {galleryElements.map((item: any, key: number) => (
            <StyledSwiperSlide
              key={key.toString()}
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              <SliderItem item={item} active={key === activeIndex} />
            </StyledSwiperSlide>
          ))}
        </Swiper>
        <StyledPaginatorContainer>
          <IconButton
            onClick={() => {
              swiperRef.slidePrev()
            }}
          >
            {culture === "he-il" ? <RightArrow /> : <LeftArrow />}
          </IconButton>
          <StyledBulletsContainer>
            {galleryElements.map((_: any, index: number) => (
              <StyledBullet
                key={index.toString()}
                onClick={() => {
                  swiperRef.slideTo(index)
                }}
                active={index === activeIndex}
              />
            ))}
          </StyledBulletsContainer>
          <IconButton
            onClick={() => {
              swiperRef.slideNext()
            }}
          >
            {culture === "he-il" ? <LeftArrow /> : <RightArrow />}
          </IconButton>
        </StyledPaginatorContainer>
      </StyledContainerInner>
    </StyledContainer>
  )
}
